import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type SecretEntry = { key: string; value: string };

const KEY_PATTERN = /^[A-Z0-9_]+$/;

/**
 * Reference count of mounted SecretsInput instances.
 * Using a count instead of a boolean makes this safe when two instances
 * co-exist (e.g. future template revisions, parallel test renders).
 * The exported predicate returns true whenever at least one instance is mounted.
 */
let _mountCount = 0;
export const isSecretsInputMounted = () => _mountCount > 0;

/**
 * Secondary defense-in-depth validator for the SecretsInput field.
 * Skips when the component is not mounted (RJSF bleed-through with
 * enableSecrets=false). Exported for unit testing; registered in extensions.ts.
 */
export const validateSecretsInput = (value: SecretEntry[]): void => {
  if (!isSecretsInputMounted()) {
    return;
  }
  if (!value || value.length === 0) {
    throw new Error(
      'At least one secret is required. Add a Key + Value pair above to continue.',
    );
  }
};

/**
 * Primary validator for the SecretsValidationField sentinel.
 * Throws when the sentinel value is 'INVALID' (enableSecrets=true,
 * SkyStage Managed, and no secrets added). Exported for unit testing;
 * registered in extensions.ts.
 */
export const validateSecretsValidationValue = (value: string): void => {
  if (value === 'INVALID') {
    throw new Error(
      'At least one secret is required. Add a Key + Value pair above to continue.',
    );
  }
};

/**
 * SecretsInput — key+value pairs for app secrets.
 *
 * On initial deploy: user enters keys + values (both required).
 *
 * On redeploy: formData is pre-filled with existing keys + empty values.
 *   Each existing secret defaults to "Keeping existing" mode — a green chip
 *   with a "Change value" button. The user must explicitly click "Change value"
 *   to reveal the password input for that secret.
 *
 *   - Keep mode (default) → value stays '' → backend skips it → GCP secret unchanged.
 *   - Change mode (user opted in) → user types new value → backend updates GCP secret.
 *
 * This makes the safe default (keep existing) explicit and obvious, rather than
 * relying on the user understanding a "(leave blank to keep existing)" placeholder.
 */
export const SecretsInput = ({
  onChange,
  rawErrors,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<SecretEntry[]>) => {
  // `ui:options.compact: true` removes the extra top margin so the field sits
  // directly under the checkbox that reveals it (used by Public App Hosting).
  const compact = uiSchema?.['ui:options']?.compact === true;
  const [secrets, setSecrets] = useState<SecretEntry[]>(
    (formData ?? []).filter(
      (s): s is SecretEntry => !!s && typeof s.key === 'string',
    ),
  );
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [newValueVisible, setNewValueVisible] = useState(false);

  // Compute once on mount: which keys were pre-filled with an empty value from
  // the redeploy formData. These are "existing" secrets that default to Keep mode.
  const [prefilledKeys] = useState<Set<string>>(
    () =>
      new Set(
        (formData ?? [])
          .filter(
            (s): s is SecretEntry =>
              !!s && typeof s.key === 'string' && s.value === '',
          )
          .map(s => s.key),
      ),
  );

  // Keys that the user has explicitly chosen to update (clicked "Change value").
  const [editingKeys, setEditingKeys] = useState<Set<string>>(() => new Set());

  // Track visibility state for each secret key (eye icon toggle)
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(
    () => new Set(),
  );

  // A row is in "keep" mode when it was pre-filled AND the user hasn't opted to change it.
  const isKeepMode = (key: string) =>
    prefilledKeys.has(key) && !editingKeys.has(key);

  const enableEdit = (key: string) => {
    setEditingKeys(prev => new Set(prev).add(key));
  };

  const toggleVisibility = (key: string) => {
    setVisibleSecrets(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isVisible = (key: string) => visibleSecrets.has(key);

  // Track mount/unmount so the extension validator can distinguish between
  // "field visible but empty" and "RJSF bleed-through with enableSecrets=false".
  useEffect(() => {
    _mountCount++;
    return () => {
      _mountCount--;
    };
  }, []);

  // Emit the current secrets on mount so RJSF has the correct initial value.
  // Validation of empty-secrets is handled by the top-level SecretsValidationField
  // (registered in extensions.ts as 'SecretsValidationField'), which Backstage
  // calls reliably because it lives outside any oneOf dependency branch. See IDP-980.
  useEffect(() => {
    onChange(secrets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (updated: SecretEntry[]) => {
    setSecrets(updated);
    onChange(updated);
  };

  const handleAdd = () => {
    const trimmedKey = newKey.trim().toUpperCase();
    setAddError(null);

    if (!trimmedKey) {
      setAddError('Secret key is required');
      return;
    }

    if (!KEY_PATTERN.test(trimmedKey)) {
      setAddError(
        'Key must only contain uppercase letters, digits, and underscores (A-Z, 0-9, _)',
      );
      return;
    }

    if (secrets.some(s => s.key === trimmedKey)) {
      setAddError(`Key "${trimmedKey}" already exists`);
      return;
    }

    if (!newValue.trim()) {
      setAddError('Secret value is required');
      return;
    }

    update([...secrets, { key: trimmedKey, value: newValue }]);
    setNewKey('');
    setNewValue('');
    setNewValueVisible(false);
  };

  const handleDelete = (index: number) => {
    const removed = secrets[index];
    setEditingKeys(prev => {
      const next = new Set(prev);
      next.delete(removed.key);
      return next;
    });
    setVisibleSecrets(prev => {
      const next = new Set(prev);
      next.delete(removed.key);
      return next;
    });
    update(secrets.filter((_, i) => i !== index));
  };

  const handleValueChange = (index: number, value: string) => {
    const updated = secrets.map((s, i) => (i === index ? { ...s, value } : s));
    update(updated);
  };

  const title = schema?.title || 'Application Secrets (Optional)';
  const description =
    schema?.description ||
    'Secrets are mounted at /var/secrets/{KEY}.txt in your container.';

  return (
    <Box sx={{ mt: compact ? 0 : 2 }}>
      <Typography
        variant="subtitle1"
        gutterBottom={!compact}
        sx={compact ? { mb: 0.25 } : undefined}
      >
        {title}
      </Typography>
      <FormHelperText sx={compact ? { mt: 0, mb: 1.5 } : { mb: 1 }}>
        {description}
      </FormHelperText>

      {secrets.length > 0 && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="caption" fontWeight="bold">
                  KEY
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" fontWeight="bold">
                  VALUE
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {secrets.map((secret, idx) => (
              <TableRow key={secret.key}>
                <TableCell>
                  <Chip label={secret.key} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  {isKeepMode(secret.key) ? (
                    // Existing secret — user hasn't opted to change it.
                    // Blank value is sent to backend which keeps the GCP secret unchanged.
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={<LockIcon />}
                        label="Keeping existing"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => enableEdit(secret.key)}
                        variant="text"
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Change value
                      </Button>
                    </Box>
                  ) : (
                    // New secret, or user clicked "Change value" for an existing one.
                    // SECURITY: Eye icon ONLY for brand-new secrets, NEVER for pre-filled keys
                    <TextField
                      type={
                        !prefilledKeys.has(secret.key) && isVisible(secret.key)
                          ? 'text'
                          : 'password'
                      }
                      size="small"
                      placeholder={
                        prefilledKeys.has(secret.key)
                          ? 'Enter new value (leave blank to keep existing)'
                          : 'Enter value'
                      }
                      value={secret.value}
                      onChange={e => handleValueChange(idx, e.target.value)}
                      fullWidth
                      variant="outlined"
                      inputProps={{
                        'aria-label': `Value for secret ${secret.key}`,
                      }}
                      InputProps={
                        !prefilledKeys.has(secret.key)
                          ? {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={`Toggle visibility for ${secret.key}`}
                                    aria-pressed={isVisible(secret.key)}
                                    onClick={() => toggleVisibility(secret.key)}
                                    onMouseDown={e => e.preventDefault()}
                                    edge="end"
                                    size="small"
                                  >
                                    {isVisible(secret.key) ? (
                                      <VisibilityOff />
                                    ) : (
                                      <Visibility />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }
                          : undefined
                      }
                      sx={{
                        '& input[type="password"]::-ms-reveal': {
                          display: 'none',
                        },
                        '& input[type="password"]::-webkit-credentials-auto-fill-button':
                          {
                            display: 'none',
                          },
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={`Remove secret ${secret.key}`}
                    onClick={() => handleDelete(idx)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          label="Key (e.g. DB_PASSWORD)"
          value={newKey}
          onChange={e => {
            setNewKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''));
            setAddError(null);
          }}
          size="small"
          variant="outlined"
          sx={{ minWidth: 200 }}
          inputProps={{ 'aria-label': 'New secret key' }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <TextField
          label="Value"
          type={newValueVisible ? 'text' : 'password'}
          value={newValue}
          onChange={e => {
            setNewValue(e.target.value);
            setAddError(null);
          }}
          size="small"
          variant="outlined"
          sx={{
            minWidth: 200,
            '& input[type="password"]::-ms-reveal': { display: 'none' },
            '& input[type="password"]::-webkit-credentials-auto-fill-button': {
              display: 'none',
            },
          }}
          inputProps={{ 'aria-label': 'New secret value' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Toggle visibility for new secret value"
                  aria-pressed={newValueVisible}
                  onClick={() => setNewValueVisible(prev => !prev)}
                  onMouseDown={e => e.preventDefault()}
                  edge="end"
                  size="small"
                >
                  {newValueVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="medium"
        >
          Add Secret
        </Button>
      </Box>

      {addError && (
        <FormHelperText error sx={{ mt: 0.5 }}>
          {addError}
        </FormHelperText>
      )}

      {secrets.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          At least one secret is required. Add a Key + Value pair above to
          continue.
        </Alert>
      )}

      {/* Only show RJSF raw errors when secrets exist (e.g., key-format errors).
          When secrets.length === 0 the warning Alert above already explains the issue. */}
      {(rawErrors?.length ?? 0) > 0 && secrets.length > 0 && (
        <FormHelperText error sx={{ mt: 0.5 }}>
          {rawErrors![0]}
        </FormHelperText>
      )}
    </Box>
  );
};

type SecretEntryItem = { key: string; value: string };

/**
 * Invisible top-level validation sentinel for the secrets configuration.
 *
 * Backstage's scaffolder does NOT invoke custom field-extension validators for
 * fields inside oneOf dependency branches (which is where the secrets field
 * lives). This component is registered at the TOP LEVEL of the scaffolder step
 * so that Backstage DOES call its validator when the user clicks NEXT/REVIEW.
 *
 * The component observes formContext.formData and emits 'INVALID' when secrets
 * validation should fail, 'OK' otherwise. The extension validator in extensions.ts
 * then blocks navigation when it receives 'INVALID'. See IDP-980.
 */
export const SecretsValidationField = ({
  onChange,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const formData = formContext?.formData ?? {};
  const enableSecrets = formData.enableSecrets as boolean | undefined;
  const secretsMode = formData.secretsMode as string | undefined;
  const secrets = formData.secrets as SecretEntryItem[] | undefined;

  // secretsMode is undefined for MCP servers (no mode picker in that template).
  // Validate whenever enableSecrets=true and the mode is SkyStage Managed OR absent
  // (i.e. MCP flow). BYOG mode (explicit non-Managed value) skips this check.
  const isInvalid =
    enableSecrets === true &&
    (secretsMode === undefined || secretsMode === 'SkyStage Managed') &&
    (!secrets || secrets.length === 0);

  // Stable ref so useEffect dependency only tracks isInvalid, not onChange identity
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    onChangeRef.current(isInvalid ? 'INVALID' : 'OK');
  }, [isInvalid]);

  // Renders nothing — validation-only field
  return <Box sx={{ display: 'none' }} />;
};
